import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icon, Text } from '@/nova/components';

import { openInApp } from './link-for';
import * as S from './LandingPageComposer.styles';
import * as L from './LandingLinks.styles';

/** An asset whose tracking link lands on (or is being pointed at) the page, with the deep link to its composer. */
export interface MadeForAsset {
  key: string;
  name: string;
  /** An in-app path (see assetLinkPath). */
  path: string;
}

/** Where the asset links stand: the page not live yet (`draft`), live, or the made-for asset's link on its way here
 *  or refused. */
export type MadeForState = 'draft' | 'live' | 'sending' | 'failed';

export interface MadeForBarProps {
  assets: MadeForAsset[];
  state: MadeForState;
  /** Open an asset's composer (in the app). Without it the names are plain text. */
  onOpen?: (path: string) => void;
  /** Point the made-for asset's link here again (offered when `failed`). */
  onRetry?: () => void;
}

/**
 * The slim strip over a landing page made for an ad (or with ads' tracking links landing on it): "Made for", each ad
 * as a deep link to its composer, then where its link stands, as a sentence of its own (never split around a link).
 */
export const MadeForBar = ({ assets, state, onOpen, onRetry }: MadeForBarProps): React.ReactElement | null => {
  const { t } = useTranslation();
  if (assets.length === 0) return null;
  const one = assets.length === 1;
  const note =
    state === 'sending'
      ? t("Sending the ad's tracking link to this page…")
      : state === 'failed'
        ? t("The ad's tracking link couldn't be sent to this page.")
        : state === 'live'
          ? one
            ? t("This page is live and the ad's tracking link goes here.")
            : t("This page is live and the ads' tracking links go here.")
          : one
            ? t("Once this page is published, the ad's tracking link goes here.")
            : t("Once this page is published, the ads' tracking links go here.");
  const tone = state === 'live' ? 'success' : state === 'failed' ? 'danger' : 'info';

  return (
    <L.LinkForBar $tone={tone} role={state === 'failed' ? 'alert' : 'status'}>
      {state === 'sending' ? (
        <S.Spinner aria-hidden="true" />
      ) : (
        <Icon name={state === 'live' ? 'check' : state === 'failed' ? 'warning' : 'info'} size={16} />
      )}
      <L.LinkForCopy>
        <L.LinkForLabel text="Made for" />
        <L.LinkForAssets>
          {assets.map((asset) =>
            onOpen ? (
              <L.AssetLink
                key={asset.key}
                href={asset.path}
                title={t('Open {{name}}', { name: asset.name })}
                onClick={(e) => openInApp(e, asset.path, onOpen)}
              >
                <Text raw text={asset.name} />
              </L.AssetLink>
            ) : (
              <Text key={asset.key} raw text={asset.name} />
            ),
          )}
        </L.LinkForAssets>
        <L.LinkForNote raw text={note} title={note} />
      </L.LinkForCopy>
      <L.LinkForActions>
        {state === 'failed' && onRetry ? (
          <Button variant="text" icon="refresh" label={t('Try again')} onClick={onRetry} />
        ) : null}
      </L.LinkForActions>
    </L.LinkForBar>
  );
};

export default MadeForBar;
